import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Grid,
  TextField,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Architecture as ArchitectureIcon,
  Storage as DatabaseIcon,
  Cloud as CloudIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import WorkflowChat from '../WorkflowChat';
import useDocuments from '../../../hooks/useDocuments';
import useNotification from '../../../hooks/useNotification';

const Architecture = ({ projectId, onSave, existingContent = '' }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [architecture, setArchitecture] = useState({
    title: 'System Architecture',
    description: 'High-level architecture design for the system',
    components: [
      {
        id: 'component-1',
        name: 'Frontend',
        type: 'UI',
        description: 'User interface built with React',
        dependencies: []
      },
      {
        id: 'component-2',
        name: 'Backend API',
        type: 'Service',
        description: 'REST API built with Node.js',
        dependencies: ['component-1']
      },
      {
        id: 'component-3',
        name: 'Database',
        type: 'Database',
        description: 'MongoDB database for data storage',
        dependencies: ['component-2']
      }
    ],
    databaseModel: {
      type: 'NoSQL',
      description: 'MongoDB for flexible document storage',
      schemas: []
    },
    deploymentModel: {
      type: 'Cloud',
      provider: 'AWS',
      description: 'Deployed on AWS using containerization',
      services: ['EC2', 'S3', 'RDS']
    },
    securityConsiderations: [
      'Authentication using JWT',
      'HTTPS encryption for all traffic',
      'Input validation to prevent injection attacks',
      'Regular security audits'
    ]
  });
  const [loading, setLoading] = useState(false);
  const [newComponent, setNewComponent] = useState({
    name: '',
    type: '',
    description: '',
    dependencies: []
  });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadContent, setUploadContent] = useState('');
  const [showChat, setShowChat] = useState(false);
  
  const { showNotification } = useNotification();
  const { createDocument } = useDocuments();
  
  // Effect to parse existing content if available
  useEffect(() => {
    if (existingContent) {
      try {
        const parsedArchitecture = JSON.parse(existingContent);
        setArchitecture(parsedArchitecture);
      } catch (error) {
        console.error('Error parsing existing architecture content:', error);
        // Try to extract architecture components from markdown/text content
        extractArchitectureFromText(existingContent);
      }
    }
  }, [existingContent]);
  
  const extractArchitectureFromText = (text) => {
    try {
      // Simple extraction of components, database info, etc. from text
      // This would be enhanced in a real implementation
      console.log('Attempting to extract architecture from text');
      
      // Example: Extract components that match patterns like "Component: Name - Description"
      const componentRegex = /Component[:\s]+([^-\n]+)[\s-]+([^\n]+)/gi;
      const databaseRegex = /Database[:\s]+([^\n]+)/gi;
      const deploymentRegex = /Deployment[:\s]+([^\n]+)/gi;
      const securityRegex = /Security[:\s]+([^\n]+)/gi;
      
      let match;
      const extractedComponents = [];
      
      // Extract components
      while ((match = componentRegex.exec(text)) !== null) {
        if (match[1] && match[2]) {
          extractedComponents.push({
            id: `component-${extractedComponents.length + 1}`,
            name: match[1].trim(),
            type: 'Component',
            description: match[2].trim(),
            dependencies: []
          });
        }
      }
      
      // Extract database info
      const databaseInfo = [];
      while ((match = databaseRegex.exec(text)) !== null) {
        if (match[1]) {
          databaseInfo.push(match[1].trim());
        }
      }
      
      // Extract deployment info
      const deploymentInfo = [];
      while ((match = deploymentRegex.exec(text)) !== null) {
        if (match[1]) {
          deploymentInfo.push(match[1].trim());
        }
      }
      
      // Extract security considerations
      const securityInfo = [];
      while ((match = securityRegex.exec(text)) !== null) {
        if (match[1]) {
          securityInfo.push(match[1].trim());
        }
      }
      
      // Update architecture state with extracted information
      if (extractedComponents.length > 0 || databaseInfo.length > 0 || 
          deploymentInfo.length > 0 || securityInfo.length > 0) {
        
        const updatedArchitecture = { ...architecture };
        
        if (extractedComponents.length > 0) {
          updatedArchitecture.components = extractedComponents;
        }
        
        if (databaseInfo.length > 0) {
          updatedArchitecture.databaseModel.description = databaseInfo.join('. ');
        }
        
        if (deploymentInfo.length > 0) {
          updatedArchitecture.deploymentModel.description = deploymentInfo.join('. ');
        }
        
        if (securityInfo.length > 0) {
          updatedArchitecture.securityConsiderations = securityInfo;
        }
        
        setArchitecture(updatedArchitecture);
        
        showNotification({
          message: 'Successfully extracted architecture information from text',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error extracting architecture from text:', error);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleAddComponent = () => {
    if (!newComponent.name || !newComponent.type) {
      showNotification({
        message: 'Component name and type are required',
        type: 'warning'
      });
      return;
    }
    
    const componentId = `component-${architecture.components.length + 1}`;
    
    const updatedComponents = [
      ...architecture.components,
      {
        id: componentId,
        ...newComponent
      }
    ];
    
    setArchitecture({
      ...architecture,
      components: updatedComponents
    });
    
    // Reset new component form
    setNewComponent({
      name: '',
      type: '',
      description: '',
      dependencies: []
    });
    
    showNotification({
      message: 'Component added successfully',
      type: 'success'
    });
  };
  
  const handleDeleteComponent = (componentId) => {
    const updatedComponents = architecture.components.filter(
      component => component.id !== componentId
    );
    
    setArchitecture({
      ...architecture,
      components: updatedComponents
    });
    
    showNotification({
      message: 'Component deleted successfully',
      type: 'info'
    });
  };
  
  const handleUpdateArchitecture = (section, field, value) => {
    if (section === 'main') {
      setArchitecture({
        ...architecture,
        [field]: value
      });
    } else if (section === 'database') {
      setArchitecture({
        ...architecture,
        databaseModel: {
          ...architecture.databaseModel,
          [field]: value
        }
      });
    } else if (section === 'deployment') {
      setArchitecture({
        ...architecture,
        deploymentModel: {
          ...architecture.deploymentModel,
          [field]: value
        }
      });
    }
  };
  
  const handleUpdateSecurity = (index, value) => {
    const updatedConsiderations = [...architecture.securityConsiderations];
    updatedConsiderations[index] = value;
    
    setArchitecture({
      ...architecture,
      securityConsiderations: updatedConsiderations
    });
  };
  
  const handleAddSecurityItem = () => {
    setArchitecture({
      ...architecture,
      securityConsiderations: [...architecture.securityConsiderations, 'New security consideration']
    });
  };
  
  const handleDeleteSecurityItem = (index) => {
    const updatedConsiderations = architecture.securityConsiderations.filter(
      (_, i) => i !== index
    );
    
    setArchitecture({
      ...architecture,
      securityConsiderations: updatedConsiderations
    });
  };
  
  const saveArchitecture = async () => {
    try {
      setLoading(true);
      
      // Convert architecture to JSON for storage
      const architectureData = JSON.stringify(architecture, null, 2);
      
      // Also create a nicely formatted markdown version for display
      const markdownContent = generateArchitectureMarkdown();
      
      // Save architecture as document
      if (projectId) {
        const document = await createDocument(projectId, {
          title: architecture.title || 'System Architecture',
          content: architectureData,
          type: 'architecture'
        });
        
        showNotification({
          message: 'Architecture saved successfully',
          type: 'success'
        });
        
        // Notify parent component
        if (onSave) {
          onSave(architectureData);
        }
      }
    } catch (error) {
      console.error('Error saving architecture:', error);
      showNotification({
        message: `Error saving architecture: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const generateArchitectureMarkdown = () => {
    let markdown = `# ${architecture.title}\n\n`;
    markdown += `${architecture.description}\n\n`;
    
    // Components section
    markdown += `## System Components\n\n`;
    architecture.components.forEach(component => {
      markdown += `### ${component.name}\n`;
      markdown += `**Type:** ${component.type}\n\n`;
      markdown += `${component.description}\n\n`;
      
      if (component.dependencies.length > 0) {
        markdown += `**Dependencies:** `;
        component.dependencies.forEach((depId, index) => {
          const dep = architecture.components.find(c => c.id === depId);
          if (dep) {
            markdown += `${dep.name}${index < component.dependencies.length - 1 ? ', ' : ''}`;
          }
        });
        markdown += `\n\n`;
      }
    });
    
    // Database section
    markdown += `## Database Model\n\n`;
    markdown += `**Type:** ${architecture.databaseModel.type}\n\n`;
    markdown += `${architecture.databaseModel.description}\n\n`;
    
    // Deployment section
    markdown += `## Deployment Model\n\n`;
    markdown += `**Type:** ${architecture.deploymentModel.type}\n\n`;
    markdown += `**Provider:** ${architecture.deploymentModel.provider}\n\n`;
    markdown += `${architecture.deploymentModel.description}\n\n`;
    
    if (architecture.deploymentModel.services.length > 0) {
      markdown += `**Services:** ${architecture.deploymentModel.services.join(', ')}\n\n`;
    }
    
    // Security section
    markdown += `## Security Considerations\n\n`;
    architecture.securityConsiderations.forEach(item => {
      markdown += `- ${item}\n`;
    });
    
    return markdown;
  };
  
  const exportArchitecture = () => {
    const markdown = generateArchitectureMarkdown();
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = `${architecture.title.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    
    URL.revokeObjectURL(url);
  };
  
  const handleUploadArchitecture = () => {
    if (!uploadContent.trim()) {
      showNotification({
        message: 'Please enter architecture content to upload',
        type: 'warning'
      });
      return;
    }
    
    try {
      // Try to parse as JSON first
      try {
        const parsedArchitecture = JSON.parse(uploadContent);
        setArchitecture(parsedArchitecture);
        setUploadDialogOpen(false);
        
        showNotification({
          message: 'Architecture JSON uploaded successfully',
          type: 'success'
        });
        return;
      } catch (jsonError) {
        console.log('Not valid JSON, trying to extract from text');
      }
      
      // If not valid JSON, try to extract from text
      extractArchitectureFromText(uploadContent);
      setUploadDialogOpen(false);
    } catch (error) {
      console.error('Error uploading architecture:', error);
      showNotification({
        message: `Error uploading architecture: ${error.message}`,
        type: 'error'
      });
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">System Architecture</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Import
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={exportArchitecture}
          >
            Export
          </Button>
          <Button 
            variant="contained" 
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading}
            onClick={saveArchitecture}
          >
            Save Architecture
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Overview" />
        <Tab label="Components" />
        <Tab label="Database" />
        <Tab label="Deployment" />
        <Tab label="Security" />
        <Tab label="Help" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, mb: 3, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>Architecture Overview</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TextField
                  fullWidth
                  label="Title"
                  value={architecture.title}
                  onChange={(e) => handleUpdateArchitecture('main', 'title', e.target.value)}
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label="Description"
                  value={architecture.description}
                  onChange={(e) => handleUpdateArchitecture('main', 'description', e.target.value)}
                  multiline
                  rows={4}
                  margin="normal"
                />
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Components Summary</Typography>
                  <Typography variant="body2">
                    {architecture.components.length} components defined
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {architecture.components.map(component => (
                      <Chip 
                        key={component.id}
                        label={component.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => setActiveTab(1)}
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Architecture Diagram</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box 
                  sx={{ 
                    height: 300, 
                    border: '1px dashed #ccc', 
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                  }}
                >
                  <ArchitectureIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" align="center">
                    Architecture Diagram
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    Create or upload a system architecture diagram
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShowChat(true)}
                  >
                    Generate with AI
                  </Button>
                </Box>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Key Technologies</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <DatabaseIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle2">
                            Database
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {architecture.databaseModel.type}: {architecture.databaseModel.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CloudIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle2">
                            Deployment
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {architecture.deploymentModel.provider} - {architecture.deploymentModel.type}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Add Component</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Component Name"
                value={newComponent.name}
                onChange={(e) => setNewComponent({ ...newComponent, name: e.target.value })}
                margin="normal"
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Component Type</InputLabel>
                <Select
                  value={newComponent.type}
                  onChange={(e) => setNewComponent({ ...newComponent, type: e.target.value })}
                  label="Component Type"
                >
                  <MenuItem value="UI">UI</MenuItem>
                  <MenuItem value="Service">Service</MenuItem>
                  <MenuItem value="Database">Database</MenuItem>
                  <MenuItem value="API">API</MenuItem>
                  <MenuItem value="Library">Library</MenuItem>
                  <MenuItem value="Middleware">Middleware</MenuItem>
                  <MenuItem value="Infrastructure">Infrastructure</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Description"
                value={newComponent.description}
                onChange={(e) => setNewComponent({ ...newComponent, description: e.target.value })}
                multiline
                rows={3}
                margin="normal"
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Dependencies</InputLabel>
                <Select
                  multiple
                  value={newComponent.dependencies}
                  onChange={(e) => setNewComponent({ ...newComponent, dependencies: e.target.value })}
                  label="Dependencies"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((depId) => {
                        const component = architecture.components.find(c => c.id === depId);
                        return component ? (
                          <Chip 
                            key={depId} 
                            label={component.name} 
                            size="small" 
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {architecture.components.map((component) => (
                    <MenuItem key={component.id} value={component.id}>
                      {component.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddComponent}
                sx={{ mt: 3 }}
              >
                Add Component
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>System Components</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {architecture.components.length > 0 ? (
                <Grid container spacing={2}>
                  {architecture.components.map((component) => (
                    <Grid item xs={12} key={component.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6">
                                {component.name}
                              </Typography>
                              <Chip 
                                label={component.type} 
                                size="small" 
                                color="primary" 
                                sx={{ mt: 0.5, mb: 1.5 }}
                              />
                              <Typography variant="body2" paragraph>
                                {component.description}
                              </Typography>
                              
                              {component.dependencies.length > 0 && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Dependencies:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    {component.dependencies.map(depId => {
                                      const dep = architecture.components.find(c => c.id === depId);
                                      return dep ? (
                                        <Chip 
                                          key={depId} 
                                          label={dep.name} 
                                          size="small" 
                                          variant="outlined"
                                        />
                                      ) : null;
                                    })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                            <Box>
                              <IconButton 
                                color="error" 
                                onClick={() => handleDeleteComponent(component.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No components defined yet. Add your first component.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Database Architecture</Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Database Type</InputLabel>
                <Select
                  value={architecture.databaseModel.type}
                  onChange={(e) => handleUpdateArchitecture('database', 'type', e.target.value)}
                  label="Database Type"
                >
                  <MenuItem value="SQL">SQL</MenuItem>
                  <MenuItem value="NoSQL">NoSQL</MenuItem>
                  <MenuItem value="Graph">Graph</MenuItem>
                  <MenuItem value="Time Series">Time Series</MenuItem>
                  <MenuItem value="In-Memory">In-Memory</MenuItem>
                  <MenuItem value="Hybrid">Hybrid</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Database Description"
                value={architecture.databaseModel.description}
                onChange={(e) => handleUpdateArchitecture('database', 'description', e.target.value)}
                multiline
                rows={4}
                margin="normal"
              />
              
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Database Selection Considerations
              </Typography>
              <TextField
                fullWidth
                label="Why this database was chosen"
                placeholder="Explain the rationale behind your database selection..."
                multiline
                rows={3}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  border: '1px dashed #ccc', 
                  borderRadius: 1, 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 250,
                  mb: 3
                }}
              >
                <DatabaseIcon sx={{ fontSize: 50, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  Database Schema Diagram
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  Upload or generate a database schema diagram
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                >
                  Upload Schema
                </Button>
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>
                Scaling Strategy
              </Typography>
              <TextField
                fullWidth
                label="How the database will scale"
                placeholder="Describe horizontal/vertical scaling, sharding, replication, etc."
                multiline
                rows={3}
                margin="normal"
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Deployment Architecture</Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Deployment Type</InputLabel>
                <Select
                  value={architecture.deploymentModel.type}
                  onChange={(e) => handleUpdateArchitecture('deployment', 'type', e.target.value)}
                  label="Deployment Type"
                >
                  <MenuItem value="Cloud">Cloud</MenuItem>
                  <MenuItem value="On-Premise">On-Premise</MenuItem>
                  <MenuItem value="Hybrid">Hybrid</MenuItem>
                  <MenuItem value="Edge">Edge</MenuItem>
                  <MenuItem value="Serverless">Serverless</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Cloud Provider</InputLabel>
                <Select
                  value={architecture.deploymentModel.provider}
                  onChange={(e) => handleUpdateArchitecture('deployment', 'provider', e.target.value)}
                  label="Cloud Provider"
                >
                  <MenuItem value="AWS">AWS</MenuItem>
                  <MenuItem value="Azure">Azure</MenuItem>
                  <MenuItem value="Google Cloud">Google Cloud</MenuItem>
                  <MenuItem value="Digital Ocean">Digital Ocean</MenuItem>
                  <MenuItem value="IBM Cloud">IBM Cloud</MenuItem>
                  <MenuItem value="Self-Hosted">Self-Hosted</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Deployment Description"
                value={architecture.deploymentModel.description}
                onChange={(e) => handleUpdateArchitecture('deployment', 'description', e.target.value)}
                multiline
                rows={4}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Cloud Services</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {architecture.deploymentModel.services.map((service, index) => (
                  <Chip 
                    key={index}
                    label={service}
                    onDelete={() => {
                      const updatedServices = architecture.deploymentModel.services.filter(
                        (_, i) => i !== index
                      );
                      
                      setArchitecture({
                        ...architecture,
                        deploymentModel: {
                          ...architecture.deploymentModel,
                          services: updatedServices
                        }
                      });
                    }}
                  />
                ))}
                <Chip 
                  icon={<AddIcon />} 
                  label="Add Service" 
                  variant="outlined"
                  onClick={() => {
                    setArchitecture({
                      ...architecture,
                      deploymentModel: {
                        ...architecture.deploymentModel,
                        services: [...architecture.deploymentModel.services, 'New Service']
                      }
                    });
                  }}
                />
              </Box>
              
              <Box 
                sx={{ 
                  border: '1px dashed #ccc', 
                  borderRadius: 1, 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 250,
                }}
              >
                <CloudIcon sx={{ fontSize: 50, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  Deployment Diagram
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  Upload or generate a deployment infrastructure diagram
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setShowChat(true)}
                >
                  Generate with AI
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {activeTab === 4 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Security Architecture</Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Security Considerations
              </Typography>
              
              {architecture.securityConsiderations.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TextField
                    fullWidth
                    value={item}
                    onChange={(e) => handleUpdateSecurity(index, e.target.value)}
                    margin="dense"
                    variant="outlined"
                    size="small"
                  />
                  <IconButton 
                    color="error" 
                    onClick={() => handleDeleteSecurityItem(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddSecurityItem}
                sx={{ mt: 1 }}
              >
                Add Security Consideration
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: 1,
                  border: '1px solid #e9ecef'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">
                    Security Recommendations
                  </Typography>
                </Box>
                
                <Typography variant="body2" paragraph>
                  • Implement proper authentication mechanisms (OAuth, JWT, etc.)
                </Typography>
                <Typography variant="body2" paragraph>
                  • Use HTTPS for all communications
                </Typography>
                <Typography variant="body2" paragraph>
                  • Apply principle of least privilege for access controls
                </Typography>
                <Typography variant="body2" paragraph>
                  • Validate all user inputs to prevent injection attacks
                </Typography>
                <Typography variant="body2" paragraph>
                  • Encrypt sensitive data at rest and in transit
                </Typography>
                <Typography variant="body2" paragraph>
                  • Implement proper error handling and logging
                </Typography>
                <Typography variant="body2" paragraph>
                  • Consider rate limiting to prevent abuse
                </Typography>
                <Typography variant="body2" paragraph>
                  • Regularly update dependencies and apply security patches
                </Typography>
              </Box>
              
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => setShowChat(true)}
                fullWidth
                sx={{ mt: 3 }}
              >
                Generate Security Recommendations
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {activeTab === 5 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Architecture Help</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <WorkflowChat
                sectionName="Architecture"
                projectId={projectId}
                placeholder="Ask about architecture best practices..."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Architecture Guidelines</Typography>
                <Typography variant="body2" paragraph>
                  • Start with defining the key components and their responsibilities
                </Typography>
                <Typography variant="body2" paragraph>
                  • Consider scalability, reliability, and security from the start
                </Typography>
                <Typography variant="body2" paragraph>
                  • Decide on appropriate database technology based on data structure and access patterns
                </Typography>
                <Typography variant="body2" paragraph>
                  • Choose deployment strategy that balances cost, performance, and operational needs
                </Typography>
                <Typography variant="body2" paragraph>
                  • Document architectural decisions and their rationale
                </Typography>
                <Typography variant="body2" paragraph>
                  • Consider future extensibility and maintenance
                </Typography>
                <Typography variant="body2" paragraph>
                  • Identify integration points with external systems
                </Typography>
                <Typography variant="body2" paragraph>
                  • Address non-functional requirements (performance, security, compliance)
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {showChat && (
        <Dialog
          open={showChat}
          onClose={() => setShowChat(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>AI-Assisted Architecture Design</DialogTitle>
          <DialogContent>
            <WorkflowChat
              sectionName="Architecture"
              projectId={projectId}
              placeholder="Describe the architecture you'd like to generate..."
              onSaveContent={(content) => {
                // Try to extract architecture information from the content
                extractArchitectureFromText(content);
                setShowChat(false);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowChat(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Architecture</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
            Paste your architecture details as JSON or in text format below. 
            The system will attempt to parse and organize the information.
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={12}
            placeholder="Paste your architecture JSON or text here..."
            value={uploadContent}
            onChange={(e) => setUploadContent(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadArchitecture}>Process</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Architecture;